import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { useMoralis } from 'react-moralis';
import { LoginResult, loginWithEmail, register } from 'utils/PlayFabAccountAPI';
import { BREAKER_CONTRACT_ADDRESS } from 'utils/abi_contract';
import { getFullImageUrl, getThumbnailUrl } from 'utils/BreakerImageAPI';
import { Account, AccountBreaker } from 'types/Account';
import { getLogin, removeLogin } from 'utils/API';
import { setUserEthAccounts } from 'utils/PointsAPI';
import type { Route } from '../routing/routes';
import { useFlags } from 'hooks/useFlags';
import { getMoralisNetworkChain } from 'utils/contract';

type AccountContextType = {
  account: Account;
  errorMessage: string;
  // a single logout function that blows up the entire world
  // instead of specific logouts for moralis and playfab
  logout: (redirectTo?: Route) => void;
  ethereum: {
    isConnecting: boolean;
    isConnected: boolean;
    login: (userId: string | null) => void;
  };
  playfab: {
    isConnecting: boolean;
    isConnected: boolean;
    login: (username: string, password: string) => Promise<LoginResult | undefined>;
    register: (
      email: string,
      password: string,
      referralCode: string | null,
      hcaptchaResponse: string,
    ) => Promise<LoginResult>;
  };
};

const defaultLogins = {
  isConnected: false,
  isConnecting: true,
  login: () => {
    throw Error('not initialized');
  },
  logout: () => Promise.resolve(),
  register: () => {
    throw Error('not initialized');
  },
};

const AccountContext = createContext<AccountContextType>({
  logout: () => {
    throw Error('No account context found');
  },
  account: {
    breakers: [],
  },
  errorMessage: '',
  ethereum: defaultLogins,
  playfab: defaultLogins,
});

const AccountProvider = ({ children }: PropsWithChildren<{}>) => {
  const { Moralis, authenticate, logout, user, isAuthUndefined, isAuthenticated } = useMoralis();
  const [moralisError, setMoralisError] = useState('');
  const [breakers, setBreakers] = useState<AccountBreaker[]>([]);
  const { identifyUser } = useFlags();

  function logoutAll(redirectTo?: Route) {
    logout().then((err) => {
      console.error('Could not log out of moralis');
      console.error(err);
    });
    setPlayfab(undefined);
    removeLogin();
    if (redirectTo) {
      // we specifically want this to be a hard refresh
      // to make sure that we don't leave any stale state
      // in the application
      window.location.href = redirectTo;
    }
  }

  const [playfab, setPlayfab] = useState<LoginResult | undefined>(() => getLogin());

  useEffect(() => {
    const getBreakers = async () => {
      const options = {
        chain: getMoralisNetworkChain(),
        address: user?.attributes?.authData?.moralisEth?.id,
        token_address: BREAKER_CONTRACT_ADDRESS!,
      } as const;

      const response = await Moralis.Web3API.account.getNFTsForContract(options);

      if (response.result && response?.result.length > 0) {
        const newBreakers = response.result.map(({ token_id }) => {
          const id = parseInt(token_id, 10);
          let isSelected = false;

          const selectedBreaker = user?.attributes?.selectedBreaker
            ? JSON.parse(user.attributes.selectedBreaker)
            : null;

          if (selectedBreaker) {
            isSelected = id === parseInt(selectedBreaker.token_id, 10);
          }

          return {
            id,
            isSelected,
            imageUrls: {
              full: getFullImageUrl(id),
              thumbnail: getThumbnailUrl(id),
            },
          };
        });

        setBreakers(newBreakers);
      }
    };

    if (user) {
      getBreakers();
    }

    // say if the user switch to another account on metamask, we should both invalidate
    // wallet connection & dustTechId login
    // @ts-ignore
    window?.ethereum?.on('accountsChanged', () => {
      setPlayfab(undefined);
      removeLogin();
    });
  }, [user, Moralis, isAuthenticated]);

  const ethAddress = user?.attributes?.authData?.moralisEth?.id;

  const handleLoginResult = async (result: LoginResult | undefined) => {
    if(result === undefined)
      return;
    setPlayfab(result);
    identifyUser();
    await setUserEthAccounts({ isAuthenticated, user }, result.userId);
  };

  return (
    <AccountContext.Provider
      value={{
        account: {
          breakers,
          ethereum: {
            address: ethAddress,
            signingMessage: user?.attributes?.authData?.moralisEth?.data,
            signature: user?.attributes?.authData?.moralisEth?.signature,
          },
          playfabAccount: playfab
            ? {
                playfabId: playfab.playfabId,
                sessionTicket: playfab.playfabSessionTicket,
              }
            : undefined,
          dusktech: playfab
            ? {
                userId: playfab.userId,
                userEmail: playfab.email,
              }
            : undefined,
        },
        errorMessage: moralisError,
        ethereum: {
          isConnected: !!ethAddress,
          isConnecting: isAuthUndefined,
          login: async (userId: string | null) => {
            authenticate({
              signingMessage: 'Welcome to the Dusk!',
              onSuccess: async (user) => {
                if (userId) {
                  setUserEthAccounts({ isAuthenticated: true, user }, userId);
                }
              },
              onError: (error) => {
                setMoralisError(error.message);
              },
            });
          },
        },
        logout: logoutAll,
        playfab: {
          isConnected: !!playfab?.playfabId,
          isConnecting: !playfab?.playfabId,
          async login(email, password) {
            const result = await loginWithEmail(email, password);
            if(result !== undefined){
              await handleLoginResult(result);
            } else {
              throw "Bad Password";
            }
            
            return result;
          },
          async register(email, password, referralCode, hcaptchaResponse) {
            const result = await register(email, password, referralCode, hcaptchaResponse);
            await handleLoginResult(result);
            return result;
          },
        },
      }}
    >
      {children}
    </AccountContext.Provider>
  );
};

const useAccount = () => {
  return useContext(AccountContext);
};

export { AccountProvider, useAccount };
